import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';

// Injected by vite.config.ts at build time; identifies the running bundle.
declare const __BUILD_ID__: string;

const CHECK_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes

export type VersionCheckResult = 'reloading' | 'current' | 'unknown';

/**
 * Compares the deployed /version.json against the running bundle. Reloads
 * (and resolves 'reloading') when a newer build is live.
 */
export async function checkVersionNow(): Promise<VersionCheckResult> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return 'unknown';
    const { buildId } = await res.json();
    if (buildId && buildId !== __BUILD_ID__) {
      window.location.reload();
      return 'reloading';
    }
    return 'current';
  } catch {
    return 'unknown';
  }
}

let invalidApiKeyHandled = false;

/**
 * Called once when Supabase rejects our publishable key (401 "Invalid API
 * key"). A stale bundle after a key rotation is the expected cause, so the
 * fix is a reload onto the new build; if the build is already current the
 * key itself is wrong and a human needs to know.
 */
export function onInvalidApiKey(): void {
  if (invalidApiKeyHandled) return;
  invalidApiKeyHandled = true;

  void checkVersionNow().then((result) => {
    if (result === 'reloading') return;

    Sentry.captureMessage('Supabase rejected the API key on the current build', {
      level: 'error',
      tags: { buildId: __BUILD_ID__, versionCheck: result },
    });

    toast.error('This session can no longer reach the server', {
      id: 'invalid-api-key',
      duration: Infinity,
      description: 'Reload the page. If the problem persists, contact support.',
      action: { label: 'Reload', onClick: () => window.location.reload() },
    });
  });
}

/**
 * Detects when a newer build has been deployed and reloads the page.
 *
 * Long-lived SPA tabs (staff leave the CRM open for weeks) keep running old
 * bundles after a deploy — including old Supabase keys/config — which caused
 * the realtime 401 retry storm found in the 2026-09-01 scan. Checks
 * /version.json (emitted per build) on an interval and whenever the tab
 * becomes visible again; a mismatch means a new deploy, so we reload.
 */
export function useVersionCheck() {
  useEffect(() => {
    if (import.meta.env.DEV) return;

    const check = () => void checkVersionNow();

    const interval = setInterval(check, CHECK_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
}

/** Mountable wrapper so App.tsx can use the check without restructuring. */
export function VersionGuard() {
  useVersionCheck();
  return null;
}
